import {
    AccountInfo,
    Configuration,
    LogLevel,
    IPublicClientApplication,
    PublicClientApplication,
    RedirectRequest,
} from '@azure/msal-browser';
import { IApiSettings } from './APISettings';

let clientApplication: PublicClientApplication | null = null;

function AuthProvider(settings: IApiSettings) {
    // One instance per app. Two instances share the cache but not the in-memory
    // interaction state, which makes them race on token renewal.
    if (clientApplication) {
        return clientApplication;
    }

    const config: Configuration = {
        auth: {
            authority: settings.authority,
            clientId: settings.clientId,
            redirectUri: settings.redirect,
            navigateToLoginRequestUrl: true,
        },
        cache: {
            // localStorage keeps the refresh token available across tabs and
            // reloads, so an idle tab can renew instead of failing.
            cacheLocation: 'localStorage',
            storeAuthStateInCookie: false,
            secureCookies: false,
        },
        system: {
            loggerOptions: {
                logLevel: LogLevel.Verbose,
                loggerCallback: (level, message, containsPii) => {
                    if (containsPii) {
                        return;
                    }
                    switch (level) {
                        case LogLevel.Error:
                            console.error(message);
                            return;
                        case LogLevel.Info:
                            console.info(message);
                            return;
                        case LogLevel.Verbose:
                            console.debug(message);
                            return;
                        case LogLevel.Warning:
                            console.warn(message);
                            return;
                    }
                },
                piiLoggingEnabled: false,
            },
        },
    };

    clientApplication = new PublicClientApplication(config);
    return clientApplication;
}

export { AuthProvider };

export const loginRequest: RedirectRequest = {
    scopes: ['User.Read'],
};

// Set while a redirect is being started. Requests run in parallel, so without
// this every one of them would call acquireTokenRedirect and MSAL would reject
// all but the first with interaction_in_progress.
let redirectStarted = false;

export async function getIdToken(
    instance: IPublicClientApplication,
    accounts: AccountInfo[],
): Promise<string> {
    if (accounts.length === 0) {
        return '';
    }

    const exp = accounts[0].idTokenClaims?.exp as number | null;
    const currentTime = Math.floor(Date.now() / 1000); // Convert to Unix timestamp
    const forceRefresh = exp != null && currentTime > exp;
    const request = {
        scopes: ['User.Read'],
        account: accounts[0],
        forceRefresh: forceRefresh,
    };

    try {
        const response = await instance.acquireTokenSilent(request);
        return response.idToken;
    } catch (error) {
        console.warn('acquireTokenSilent failed', error);

        // A popup cannot be opened from a background refetch, so re-authenticate
        // with a full page redirect instead.
        if (!redirectStarted) {
            redirectStarted = true;
            try {
                await instance.acquireTokenRedirect(request);
            } catch (redirectError) {
                redirectStarted = false;
                console.error('acquireTokenRedirect failed', redirectError);
            }
        }

        return '';
    }
}
